import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { IconChevronRight, IconInfoCircle } from "@tabler/icons-react";

import { UserAvatar } from "@/components/common/UserAvatar";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { DataTable } from "@/components/ui/data-table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  fetchPerformanceCreator,
  fetchPerformanceCreators,
  type CreatorView,
  type CurrencyView,
} from "@/services/metaPerformance.service";
import { cn } from "@/lib/utils";

import type { PerfScope } from "./AdPerformancePage";
import { MixedMoney, Sparkline } from "./bits";
import { performanceColumns } from "./columns";
import { fmtCross, fmtInt, fmtMoney, fmtPct, fmtRoas } from "./format";

/**
 * Creators ranked by the money the brand made from their ads on Reveshare,
 * smoothed for volume and consistency so one big day cannot sit on top.
 */
export function CreatorsTab({ scope, onCurrency }: { scope: PerfScope; onCurrency: (c: CurrencyView) => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["meta-performance", "creators", scope.days],
    queryFn: () => fetchPerformanceCreators(scope.days),
  });
  useEffect(() => {
    if (q.data) onCurrency(q.data.currency);
  }, [q.data, onCurrency]);
  const cur = q.data?.currency.account ?? "";

  const columns = useMemo<ColumnDef<CreatorView>[]>(
    () => [
      {
        id: "rank",
        header: "#",
        meta: { fit: true, cellClassName: "text-right" },
        cell: ({ row }) => <span className="tabular-nums text-muted-foreground">{row.original.rank ?? "—"}</span>,
      },
      {
        id: "creator",
        header: "Creator",
        meta: { width: "w-[24%]" },
        cell: ({ row }) => {
          const c = row.original.creator;
          return (
            <div className="flex min-w-0 items-center gap-2.5">
              <UserAvatar src={c.profileImage} name={`${c.firstName} ${c.lastName}`} className="size-9 shrink-0" />
              <div className="min-w-0">
                <div className="truncate font-medium text-foreground">{c.firstName} {c.lastName}</div>
                <div className="truncate text-xs text-muted-foreground">{c.username ? `@${c.username} · ` : ""}{row.original.adsCount} ad{row.original.adsCount === 1 ? "" : "s"}</div>
              </div>
            </div>
          );
        },
      },
      {
        id: "score",
        header: () => <ScoreHeader />,
        meta: { fit: true, cellClassName: "text-right" },
        cell: ({ row }) => {
          const r = row.original;
          if (r.score === null) return <span className="text-xs text-muted-foreground">{r.unscoredReason === "CURRENCY_CONFLICT" ? "Mixed currencies" : "No spend yet"}</span>;
          return <span className="tabular-nums font-semibold text-foreground">{fmtMoney(r.score, currencyOf(r) || cur, true)}</span>;
        },
      },
      { id: "revenue", header: "Revenue", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => <span className="tabular-nums"><MixedMoney m={row.original.revenue} fallbackCurrency={cur} /></span> },
      { id: "orders", header: "Orders", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => <span className="tabular-nums">{fmtInt(row.original.orders)}</span> },
      { id: "spend", header: "Spend", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => <span className="tabular-nums">{fmtMoney(row.original.meta.spend, cur)}</span> },
      { id: "roas", header: "ROAS (Meta)", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => <span className="tabular-nums">{fmtRoas(row.original.meta.roas)}</span> },
      { id: "netRoas", header: "Net ROAS", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => <span className="tabular-nums">{fmtCross(row.original.netRoas)}</span> },
      { id: "ctr", header: "CTR", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => <span className="tabular-nums">{fmtPct(row.original.meta.ctr)}</span> },
      {
        id: "consistency",
        header: "Weeks with sales",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2">
            <Sparkline values={row.original.weekly} />
            <span className="tabular-nums text-muted-foreground">{row.original.weeksWithSales} of {row.original.weeksInWindow}</span>
          </span>
        ),
      },
      {
        id: "open",
        header: "",
        meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
        cell: () => <IconChevronRight size={16} stroke={1.75} className="inline-block text-muted-foreground" aria-hidden />,
      },
    ],
    [cur],
  );

  return (
    <>
      <DashboardCard title="Creators by what they earned you" bodyClassName="px-0 pb-0">
        <DataTable
          columns={columns}
          data={q.data?.creators ?? []}
          isLoading={q.isLoading}
          getRowId={(r) => r.affiliateId}
          emptyText="No creator ads yet. Launch approved content from a program, or request a TikTok video for ads, and creators rank here."
          onRowClick={(r) => setOpen(r.affiliateId)}
        />
      </DashboardCard>
      <CreatorSheet affiliateId={open} scope={scope} onClose={() => setOpen(null)} />
    </>
  );
}

function currencyOf(r: CreatorView): string {
  return r.revenue.conflict ? "" : r.revenue.currency;
}

function ScoreHeader() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 underline-offset-4 hover:underline">
          Score <IconInfoCircle size={13} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-xs leading-relaxed" align="end">
        <p className="font-medium text-foreground">Revenue per active day, smoothed</p>
        <p className="mt-1 text-muted-foreground">
          Reveshare-attributed revenue divided by the days the creator's ads were spending, pulled toward the brand average while the creator has only a few days of data (a seven-day prior), then multiplied by a consistency factor from the share of weeks with at least one order (0.5 to 1).
        </p>
        <p className="mt-1 text-muted-foreground">One big sale on one day cannot hold the top spot: the prior dampens it and the consistency factor halves it.</p>
      </PopoverContent>
    </Popover>
  );
}

function CreatorSheet({ affiliateId, scope, onClose }: { affiliateId: string | null; scope: PerfScope; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["meta-performance", "creator", affiliateId, scope.days],
    queryFn: () => fetchPerformanceCreator(affiliateId!, scope.days),
    enabled: !!affiliateId,
  });
  const d = q.data;
  const cur = d?.currency.account ?? "";
  const columns = useMemo(() => performanceColumns("ad", cur), [cur]);
  const c = d?.creator;
  return (
    <Sheet open={!!affiliateId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-3xl">
        <SheetHeader className="pr-6">
          <SheetTitle className="text-[15px]">{c ? `${c.creator.firstName} ${c.creator.lastName}` : "Creator"}</SheetTitle>
          <SheetDescription className="text-xs">
            {c ? `${c.rank ? `Ranked #${c.rank} · ` : ""}${c.adsCount} ad${c.adsCount === 1 ? "" : "s"} · last ${scope.days} days` : ""}
          </SheetDescription>
        </SheetHeader>
        {!d || !c ? (
          <div className="mt-6 h-40 animate-pulse rounded-md bg-muted" />
        ) : (
          <div className="mt-5 space-y-6">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              <F label="Revenue (Reveshare)" value={<MixedMoney m={c.revenue} fallbackCurrency={cur} />} />
              <F label="Orders" value={fmtInt(c.orders)} />
              <F label="Spend" value={fmtMoney(c.meta.spend, cur)} />
              <F label="ROAS (Meta)" value={fmtRoas(c.meta.roas)} />
              <F label="Net ROAS" value={fmtCross(c.netRoas)} hint="Reveshare revenue over Meta spend" />
              <F label="True ROAS" value={fmtCross(c.trueRoas)} hint="Revenue over spend plus usage fees and commission" />
              <F label="Fees paid" value={<MixedMoney m={c.feesPaid} fallbackCurrency={cur} />} />
              <F label="Commission owed" value={<MixedMoney m={c.commission} fallbackCurrency={cur} />} />
            </dl>

            <section className="rounded-lg border border-border p-3 text-xs">
              <div className="font-medium text-foreground">How the score was built</div>
              <ul className="mt-1.5 grid gap-x-6 gap-y-1 text-muted-foreground sm:grid-cols-2">
                <li>Active ad-days: <b className="tabular-nums text-foreground">{c.activeDays}</b></li>
                <li>Weeks with sales: <b className="tabular-nums text-foreground">{c.weeksWithSales} of {c.weeksInWindow}</b></li>
                <li>Revenue per active day after smoothing: <b className="tabular-nums text-foreground">{c.shrunkRevenuePerDay != null ? fmtMoney(c.shrunkRevenuePerDay, currencyOf(c) || cur) : "—"}</b></li>
                <li>Consistency factor: <b className="tabular-nums text-foreground">{c.consistency != null ? c.consistency.toFixed(2) : "—"}</b></li>
                <li className="sm:col-span-2">Score: <b className="tabular-nums text-foreground">{c.score != null ? fmtMoney(c.score, currencyOf(c) || cur) : c.unscoredReason === "CURRENCY_CONFLICT" ? "not scored, sales in mixed currencies" : "not scored, no spend yet"}</b></li>
              </ul>
              <div className="mt-2 flex items-center gap-2 text-muted-foreground">
                Weekly revenue <Sparkline values={c.weekly} className="h-6" />
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-[13px] font-semibold text-foreground">Their ads</h3>
              <div className={cn("overflow-hidden rounded-lg border border-border")}>
                <DataTable columns={columns} data={d.ads} isLoading={false} getRowId={(r) => r.id} emptyText="No ads" onRowClick={(r) => scope.openAd(r.id)} />
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function F({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="min-w-0" title={hint}>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="truncate text-lg font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}
